import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { APIClient } from '../utils/APIClient';
import { LocalStorage } from '../utils/LocalStorage';
import { API_URL } from '../settings/local_settings';

// Status que requieren redireccion
const STATUS_NEVER_ACTIVE = 'Never Active';
const STATUS_CHURNED = 'Churned';

// Paginas donde no se debe hacer el check (ya esta en la pagina de error)
const EXCLUDED_PATHS = ['/free-trial-ended', '/subscription-ended', '/login', '/signup', '/agency/subscription'];

// Debounce en milisegundos (30 segundos)
const DEBOUNCE_MS = 30000;

export const useAgencyStatusRefresh = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const lastCheckRef = useRef(0);

    const shouldSkipCheck = useCallback(() => {
        // No hay datos de usuario
        if (!LocalStorage.hasUserData()) {
            return true;
        }

        // No es AGENCY_USER
        if (LocalStorage.fetchUserType() !== 'AGENCY_USER') {
            return true;
        }

        // Esta en una pagina excluida
        if (EXCLUDED_PATHS.includes(location.pathname)) {
            return true;
        }

        // Debounce: paso menos de 30 segundos desde el ultimo check
        const now = Date.now();
        if (now - lastCheckRef.current < DEBOUNCE_MS) {
            return true;
        }

        return false;
    }, [location.pathname]);

    const checkAgencyStatus = useCallback(() => {
        if (shouldSkipCheck()) {
            return;
        }

        lastCheckRef.current = Date.now();

        const url = API_URL + 'get-logged-user-data/';
        new APIClient(url).call('GET').then((data) => {
            if (!data || data.status !== 'OK') {
                // Error de red o respuesta invalida, ignorar silenciosamente
                return;
            }

            const newStatus = data.agency_status;
            const currentStatus = LocalStorage.fetchAgencyStatus();

            // Si el status cambio, actualizar localStorage
            if (newStatus && newStatus !== currentStatus) {
                LocalStorage.updateAgencyStatus(newStatus);
            }

            // Sincronizar is_dashbo_mcp
            if (data.is_dashbo_mcp !== undefined) {
                LocalStorage.updateIsDashboMcp(data.is_dashbo_mcp);
            }

            // Sincronizar free_trial_days_remaining
            if (data.free_trial_days_remaining !== undefined) {
                LocalStorage.updateFreeTrialDaysRemaining(data.free_trial_days_remaining);
            }

            // Redirigir segun el status
            if (newStatus === STATUS_NEVER_ACTIVE) {
                navigate('/free-trial-ended');
            } else if (newStatus === STATUS_CHURNED) {
                navigate('/subscription-ended');
            }
        }).catch(() => {
            // Error de red, ignorar silenciosamente para no afectar la sesion del usuario
        });
    }, [shouldSkipCheck, navigate]);

    useEffect(() => {
        // Check inicial al montar
        checkAgencyStatus();

        // Handler para cuando la tab vuelve a ser visible
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkAgencyStatus();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [checkAgencyStatus]);
};
